import React, { useState, useEffect } from 'react';
import { Cookie, X, Check, Settings } from 'lucide-react';
import { Button } from './ui/button';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Petit délai pour ne pas afficher immédiatement
      setTimeout(() => setShowBanner(true), 1000);
    } else if (consent === 'accepted') {
      loadAnalytics();
    }
  }, []);

  const loadAnalytics = () => {
    // Google Analytics
    if (!document.getElementById('ga-script')) {
      const gaScript = document.createElement('script');
      gaScript.id = 'ga-script';
      gaScript.async = true;
      gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-BHPND0DMGJ';
      document.head.appendChild(gaScript);

      const gaInlineScript = document.createElement('script');
      gaInlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-BHPND0DMGJ');
      `;
      document.head.appendChild(gaInlineScript);
    }

    // Microsoft Clarity
    if (!document.getElementById('clarity-script')) {
      const clarityScript = document.createElement('script');
      clarityScript.id = 'clarity-script';
      clarityScript.innerHTML = `
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "v5n41teunb");
      `;
      document.head.appendChild(clarityScript);
    }
  };

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    loadAnalytics();
    setShowBanner(false);
  };

  const refuseAll = () => {
    localStorage.setItem('cookieConsent', 'refused');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4 animate-slide-up">
      <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Main Banner */}
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-cyan/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-primary-blue dark:text-primary-cyan" />
            </div>
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                🍪 Cookies
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm leading-relaxed">
                Nous utilisons des cookies pour analyser le trafic et améliorer votre expérience.
              </p>
              
              {/* Details section */}
              {showDetails && (
                <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl space-y-2 sm:space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Cookies essentiels</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Nécessaires au fonctionnement</p>
                    </div>
                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full">Actifs</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm">Analytics</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Google Analytics & Clarity</p>
                    </div>
                    <span className="text-xs bg-primary-cyan/10 text-primary-blue dark:text-primary-cyan px-2 py-1 rounded-full">Optionnel</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center sm:justify-end">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hover:text-primary-blue dark:hover:text-primary-cyan flex items-center gap-1 transition-colors justify-center sm:justify-start"
            >
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
              {showDetails ? 'Masquer' : 'Détails'}
            </button>
            <div className="flex gap-2 sm:gap-3">
              <Button
                onClick={refuseAll}
                variant="outline"
                className="flex-1 sm:flex-none border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full px-4 sm:px-6 text-sm"
              >
                <X className="w-4 h-4 mr-1 sm:mr-2" />
                Refuser
              </Button>
              <Button
                onClick={acceptAll}
                className="flex-1 sm:flex-none bg-primary-blue hover:bg-primary-blue/90 text-white rounded-full px-4 sm:px-6 text-sm"
              >
                <Check className="w-4 h-4 mr-1 sm:mr-2" />
                Accepter
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
